import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActionToolbar = ({ onPrintSheets, onExportReports, onGenerateCompliance }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scanCount, setScanCount] = useState(0);

  const handleBarcodeScanning = () => {
    setIsScanning(!isScanning);
    if (!isScanning) {
      // Simulate scanning process
      const interval = setInterval(() => {
        setScanCount(prev => prev + 1);
      }, 2000);
      
      setTimeout(() => {
        clearInterval(interval);
        setIsScanning(false);
        setScanCount(0);
      }, 10000);
    }
  };

  const quickActions = [
    {
      id: 'scan',
      label: isScanning ? `Scanning... (${scanCount})` : 'Start Barcode Scan',
      icon: isScanning ? 'Loader2' : 'Scan',
      variant: isScanning ? 'default' : 'outline',
      onClick: handleBarcodeScanning,
      disabled: false,
      tooltip: 'Start mobile barcode scanning session'
    },
    {
      id: 'print',
      label: 'Print Audit Sheets',
      icon: 'Printer',
      variant: 'outline',
      onClick: onPrintSheets,
      disabled: false,
      tooltip: 'Generate printable audit verification sheets'
    },
    {
      id: 'export',
      label: 'Export Reports',
      icon: 'Download',
      variant: 'outline',
      onClick: onExportReports,
      disabled: false,
      tooltip: 'Export audit data and verification reports'
    },
    {
      id: 'compliance',
      label: 'Generate Compliance',
      icon: 'FileCheck',
      variant: 'default',
      onClick: onGenerateCompliance,
      disabled: false,
      tooltip: 'Generate SOX compliance documentation'
    }
  ];

  const keyboardShortcuts = [
    { key: 'Ctrl + S', action: 'Start Scanning' },
    { key: 'Ctrl + P', action: 'Print Sheets' },
    { key: 'Ctrl + E', action: 'Export Reports' },
    { key: 'Enter', action: 'Verify Selected' },
    { key: 'Tab', action: 'Navigate Assets' },
    { key: 'Esc', action: 'Cancel Action' }
  ];

  const integrationStatus = [
    {
      system: 'ERP Integration',
      status: 'Connected',
      lastSync: '2 minutes ago',
      icon: 'Database',
      color: 'text-success'
    },
    {
      system: 'Mobile Scanner',
      status: isScanning ? 'Active' : 'Ready',
      lastSync: isScanning ? 'Live' : '5 minutes ago',
      icon: 'Smartphone',
      color: isScanning ? 'text-accent' : 'text-success'
    },
    {
      system: 'Backup Service',
      status: 'Synced',
      lastSync: '1 hour ago',
      icon: 'Cloud',
      color: 'text-success'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Quick Actions</h3>
            <p className="text-sm text-muted-foreground">
              Streamlined audit workflow tools and shortcuts
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 text-sm text-muted-foreground">
              <Icon name="Zap" size={16} />
              <span>Keyboard shortcuts enabled</span>
            </div>
          </div>
        </div>

        {/* Quick Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {quickActions?.map((action) => (
            <div key={action?.id} className="relative group">
              <Button
                variant={action?.variant}
                onClick={action?.onClick}
                disabled={action?.disabled}
                className="w-full h-20 flex flex-col items-center justify-center space-y-2"
              >
                <Icon 
                  name={action?.icon} 
                  size={24} 
                  className={action?.icon === 'Loader2' && isScanning ? 'animate-spin' : ''}
                />
                <span className="text-sm font-medium">{action?.label}</span>
              </Button>
              
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-popover text-popover-foreground text-xs rounded-md shadow-elevated opacity-0 group-hover:opacity-100 transition-smooth pointer-events-none whitespace-nowrap z-50">
                {action?.tooltip}
              </div>
            </div>
          ))}
        </div>

        {/* Integration Status */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-card-foreground mb-3">System Integration Status</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {integrationStatus?.map((system, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                <div className={system?.color}>
                  <Icon name={system?.icon} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-card-foreground">
                      {system?.system}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      system?.status === 'Connected' || system?.status === 'Synced' || system?.status === 'Ready' ?'bg-success/20 text-success'
                        : system?.status === 'Active' ?'bg-accent/20 text-accent' :'bg-error/20 text-error'
                    }`}>
                      {system?.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Last sync: {system?.lastSync}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Keyboard Shortcuts */}
        <div>
          <h4 className="text-sm font-medium text-card-foreground mb-3">Keyboard Shortcuts</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {keyboardShortcuts?.map((shortcut, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                <span className="text-xs text-muted-foreground">{shortcut?.action}</span>
                <kbd className="px-2 py-1 text-xs font-mono bg-card border border-border rounded">
                  {shortcut?.key}
                </kbd>
              </div>
            ))}
          </div>
        </div>

        {/* Scanning Progress */}
        {isScanning && (
          <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Icon name="Scan" size={16} className="text-accent animate-pulse" />
                <span className="text-sm font-medium text-accent">Barcode Scanning Active</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsScanning(false)}
                className="text-accent hover:text-accent"
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex-1 bg-muted rounded-full h-2">
                <div 
                  className="bg-accent h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(scanCount / 5) * 100}%` }}
                />
              </div>
              <span className="text-sm text-muted-foreground">
                {scanCount} assets scanned
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickActionToolbar;